import { z } from "zod";
import { localized } from "../../locales";
import { ZodNumberNullable } from "../zod-types/zod-number-nullable";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

/**
 * A decision on one approval — the "Prüfen" of an invoice: accept, reject, or take a decision
 * back to pending. A rejection says why. An acceptance carries the cost allocation the approver
 * settled on and, optionally, a Kürzung: the net amount the invoice is paid on instead, with a
 * mandatory reason. `skipChain` is a default approver's explicit decision ahead of an open chain.
 */
export const incomingInvoiceApprovalDecisionSchema = z
	.object({
		state: z.enum(["approved", "rejected", "pending"]),
		comment: ZodStringNullable,
		allocations: z.array(z.object({ costCenterId: z.uuid(), amount: z.number() })).optional(),
		reducedNetAmount: ZodNumberNullable.nullable().optional(),
		reductionReason: ZodStringNullable.optional(),
		skipChain: z.boolean().optional(),
	})
	.refine((value) => value.state !== "rejected" || !!value.comment?.trim(), {
		path: ["comment"],
		message: localized("incoming_invoices.rejection_comment_required"),
	})
	.refine(
		(value) =>
			value.reducedNetAmount === null ||
			value.reducedNetAmount === undefined ||
			!!value.reductionReason?.trim(),
		{
			path: ["reductionReason"],
			message: localized("incoming_invoices.decision.reduction_reason_required"),
		},
	);

export type IncomingInvoiceApprovalDecisionSchema = z.infer<
	typeof incomingInvoiceApprovalDecisionSchema
>;

/**
 * The "Prüfen" form: the verdict, the comment, the cost allocation with each row's share — kept in
 * step with its amount as the allocation form does — and the Kürzung, switched on by `isReduced`.
 * It is turned into the decision the route takes when submitted.
 */
export const incomingInvoiceReviewFormSchema = z
	.object({
		state: z.enum(["approved", "rejected"]),
		comment: ZodStringNullable,
		allocations: z.array(
			z.object({ costCenterId: z.uuid(), amount: z.number(), share: z.number() }),
		),
		isReduced: z.boolean(),
		reducedNetAmount: ZodNumberNullable.nullable(),
		reductionReason: ZodStringNullable,
	})
	.refine((value) => value.state !== "rejected" || !!value.comment?.trim(), {
		path: ["comment"],
		message: localized("incoming_invoices.rejection_comment_required"),
	})
	.refine(
		(value) => value.state !== "approved" || !value.isReduced || !!value.reductionReason?.trim(),
		{
			path: ["reductionReason"],
			message: localized("incoming_invoices.decision.reduction_reason_required"),
		},
	)
	.refine(
		(value) =>
			value.state !== "approved" ||
			!value.isReduced ||
			(value.reducedNetAmount !== null && value.reducedNetAmount > 0),
		{
			path: ["reducedNetAmount"],
			message: localized("incoming_invoices.decision.reduced_amount_required"),
		},
	);

export type IncomingInvoiceReviewFormSchema = z.infer<typeof incomingInvoiceReviewFormSchema>;
